import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
  RowNumberCell,
  toast,
} from "../../components/ui";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import { useDispatch, useSelector } from "react-redux";
import { ZoomRecordingResponse } from "../../api/models/ZoomRecordingResponse";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { OpenAPI, ZoomService } from "../../api";
import { ZoomRecordingRequest } from "../../api/models/ZoomRecordingRequest";
import React from "react";

const DEFAULT_ROWS_PER_PAGE = 25;

const TABLE_COLUMNS = [
  { id: "calleeName", label: "Callee name", sortable: false },
  { id: "calleeNumber", label: "Callee number", sortable: false },
  { id: "callerName", label: "Caller name", sortable: false },
  { id: "callerNumber", label: "Caller number", sortable: false },
  { id: "direction", label: "Direction", sortable: false },
  { id: "duration", label: "Duration", sortable: false },
  { id: "dateTime", label: "Date", sortable: false },
];

export default function ZoomRecordings() {
  const user = useSelector((state: any) => state.user?.user);
  const zoomUserName = user?.zoomUserName?.trim() || "";

  const [recordings, setRecordings] = useState<ZoomRecordingResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const dispatch = useDispatch();

  const loadRecordings = useCallback(() => {
    if (!zoomUserName) {
      setRecordings([]);
      setPage(0);
      return;
    }

    setLoading(true);
    dispatch(startSpinner());
    const request = {
      username: zoomUserName,
    } as ZoomRecordingRequest;

    ZoomService.postApiZoomRecordings(request)
      .then((next) => {
        setRecordings(next);
        setPage(0);
      })
      .catch(() => {
        setRecordings([]);
        toast.error("Could not load Zoom recordings.");
      })
      .finally(() => {
        setLoading(false);
        dispatch(stopSpinner());
      });
  }, [dispatch, zoomUserName]);

  useEffect(() => {
    loadRecordings();
  }, [loadRecordings]);

  const maxPage = Math.max(0, Math.ceil(recordings.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const paginatedRecordings = useMemo(
    () =>
      recordings.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage
      ),
    [recordings, safePage, rowsPerPage]
  );

  const handleDownloadTranscript = (
    recording: ZoomRecordingResponse,
    e?: React.MouseEvent
  ) => {
    e?.stopPropagation();
    dispatch(startSpinner());
    const request = {
      recordingId: recording.id,
    } as ZoomRecordingRequest;

    fetch(`${OpenAPI.BASE}/api/Zoom/downloadTranscript`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    })
      .then((response: any) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        return response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `${recording.recordingDateTime}.docx`;
          document.body.appendChild(a);
          a.click();
          toast.success("Transcript downloaded.");
        });
      })
      .catch(() => {
        toast.error("Could not download transcript.");
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const handleDownloadRecording = (
    recording: ZoomRecordingResponse,
    e?: React.MouseEvent
  ) => {
    e?.stopPropagation();
    if (!recording.hasRecordingDownload) {
      toast.warning("No audio recording is available for this call.");
      return;
    }

    dispatch(startSpinner());
    const request = {
      recordingId: recording.id,
    } as ZoomRecordingRequest;

    fetch(`${OpenAPI.BASE}/api/Zoom/downloadRecording`, {
      method: "POST",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    })
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        const disposition = response.headers.get("content-disposition") ?? "";
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const fileName = match?.[1] ?? `zoom-recording-${recording.id}.mp3`;
        return response.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Recording downloaded.");
      })
      .catch(() => {
        toast.error("Could not download recording.");
      })
      .finally(() => dispatch(stopSpinner()));
  };

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="Zoom recordings"
        subtitle="Browse recordings and download transcripts or audio."
      />

      {!zoomUserName ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            No Zoom account is assigned to your profile. Ask an admin to set
            your Zoom user on the user edit page.
          </Typography>
        </Paper>
      ) : (
        <AppTable
          columns={TABLE_COLUMNS}
          showRowNumber
          loading={loading}
          loadingMessage="Loading recordings..."
          maxHeight="calc(100vh - 280px)"
          trailingHeadCells={
            <AppTableHeadCell align="right" sx={{ width: 96 }}>
              Actions
            </AppTableHeadCell>
          }
          extraColumnCount={1}
          isEmpty={!loading && recordings.length === 0}
          emptyTitle="No recordings found"
          emptyDescription="Check back later for new Zoom recordings."
          pagination={{
            count: recordings.length,
            page: safePage,
            rowsPerPage,
            rowsPerPageOptions: [10, 25, 50, 100],
            onPageChange: setPage,
            onRowsPerPageChange: (next) => {
              setRowsPerPage(next);
              setPage(0);
            },
          }}
        >
          {paginatedRecordings.map((recording, index) => (
            <AppTableRow key={recording.id ?? index} tabIndex={-1}>
              <RowNumberCell value={safePage * rowsPerPage + index + 1} />
              <AppTableCell>{recording.calleeName || "—"}</AppTableCell>
              <AppTableCell>{recording.calleeNumber || "—"}</AppTableCell>
              <AppTableCell>{recording.callerName || "—"}</AppTableCell>
              <AppTableCell>{recording.callerNumber || "—"}</AppTableCell>
              <AppTableCell>{recording.direction || "—"}</AppTableCell>
              <AppTableCell>{recording.duration ?? "—"}</AppTableCell>
              <AppTableCell>
                {recording.recordingDateTime
                  ? new Date(recording.recordingDateTime).toLocaleDateString(
                      "en-US"
                    )
                  : "—"}
              </AppTableCell>
              <AppTableCell align="right">
                <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                  <Tooltip
                    title={
                      recording.hasRecordingDownload
                        ? "Download recording"
                        : "Recording audio not available"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!recording.hasRecordingDownload}
                        onClick={(e) => handleDownloadRecording(recording, e)}
                        aria-label="Download recording"
                      >
                        <GraphicEqOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download transcript">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => handleDownloadTranscript(recording, e)}
                      aria-label="Download transcript"
                    >
                      <DownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AppTableCell>
            </AppTableRow>
          ))}
        </AppTable>
      )}
    </Box>
  );
}
